import { useTranslation } from "react-i18next";
import Term from "../../term/Term";
import styles from "../css/LearningReview.module.css";
import { LearningType } from "../model/LearningType";
import { LearningHistoryRange } from "../model/learningHistoryRange";
import LearningRetry from "../model/LearningRetry";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { ReactComponent as AlertIcon } from "../../../img/iconImgAllAlert.svg";
import { useEffect, useState } from "react";
import ListSettingView from "../../common/ListSettingView";
import {
  NO_LIMIT,
  getLearningAnswerTypeText,
  getLearningRangeText,
  getLearningTypeText,
} from "../util/learning";
import BasicMenu from "../../common/BasicMenu";
import { fetchLeanredTerms as fetchLeanredTermsFromAPI } from "../util/learningAPI";
import {
  getLearningIntervalUnitCountFromLocalStorage,
  getLearningReviewLatestAnswerTypeFromLocalStorage,
  getLearningReviewLatestRangeFromLocalStorage,
  getLearningReviewLatestTypeFromLocalStroage,
  setLearningIntervalUnitCountToLocalStroage,
  setLearningReviewLatestAnswerTypeToLocalStroage,
  setLearningReviewLatestRangeToLocalStroage,
  setLearningReviewLatestType,
} from "../../settings/util/learningSettings";
import { toDecimalString } from "../../common/strings";
import CountPicker from "../../common/CountPicker";
import { CountPickerStyle } from "../../common/CountPickerStyle";
import { LearningAnswerType } from "../model/LearningAnswerType";
import OnevocaCheckBoxContainer from "../../common/OnevocaCheckBoxContainer";
import BigButton from "../../common/BigButton";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { makeSelectedGroupsString } from "../../groups/groups";
import Group from "../../groups/Group";
import LearningReviewGroupSelectScreen from "./LearningReviewGroupSelectScreen";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface LearningReviewProps {
  open: boolean;
  range?: LearningHistoryRange;
  onClickStartButton: (learningRetry: LearningRetry) => void;
  onClose: () => void;
}

export default function LearningReview(props: LearningReviewProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [originTerms, setOriginTerms] = useState<Term[]>([]);
  const [filteredTerms, setFilteredTerms] = useState<Term[]>([]);

  const [parts, setParts] = useState<string[]>([]);
  const [termCount, setTermCount] = useState<number>(0);

  const [intervalUnitCountString, setIntervalUnitCountString] = useState("");
  const [currentSelectedRange, setCurrentSelectedRange] =
    useState<LearningHistoryRange>(LearningHistoryRange.day);

  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedGroupsString, setSelectedGroupsString] = useState("");
  const [noTermsInSelectedGroups, setNoTermsInSelectedGroups] = useState(false);

  const [currentSelectedLearningType, setCurrentSelectedLearningType] =
    useState<LearningType>(LearningType.spellingQuiz);
  const [currentSelectedAnswerType, setCurrentSelectedAnswerType] = useState<
    LearningAnswerType[]
  >([LearningAnswerType.correct, LearningAnswerType.incorrect]);

  const [openLearningRange, setOpenLearningRange] = useState<boolean>(false);
  const [openIntervalUnitCountPicker, setOpenIntervalUnitCountPicker] =
    useState<boolean>(false);
  const [openGroupSelect, setOpenGroupSelect] = useState<boolean>(false);
  const [openLearningType, setOpenLearningType] = useState<boolean>(false);
  const [openAnswerType, setOpenAnswerType] = useState<boolean>(false);

  useEffect(() => {
    if (props.range === undefined) {
      const range = getLearningReviewLatestRangeFromLocalStorage();
      setCurrentSelectedRange(range);
    } else {
      setCurrentSelectedRange(props.range);
    }
    updateIntervalUnitCountString();
    setCurrentSelectedLearningType(
      getLearningReviewLatestTypeFromLocalStroage()
    );
    setCurrentSelectedAnswerType(
      getLearningReviewLatestAnswerTypeFromLocalStorage()
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    fetchLeanredTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedRange]);

  useEffect(() => {
    setSelectedGroupsString(makeSelectedGroupsString(selectedGroups));
    makeFilteredTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups]);

  useEffect(() => {
    makeFilteredTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originTerms]);

  useEffect(() => {
    const count = filteredTerms.length;
    const localized = t("LearningReviewTermCountText");
    const parts = localized.split("{{count}}");
    setTermCount(count);
    setParts(parts);
  }, [filteredTerms, t]);

  useEffect(() => {
    makeFilteredTerms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedAnswerType]);

  function makeFilteredTerms() {
    const newTerms: Term[] = [];
    if (
      currentSelectedAnswerType.some(
        (type) => type === LearningAnswerType.correct
      )
    ) {
      newTerms.push(...originTerms.filter((term) => term.isCorrectAnswer));
    }
    if (
      currentSelectedAnswerType.some(
        (type) => type === LearningAnswerType.incorrect
      )
    ) {
      newTerms.push(...originTerms.filter((term) => !term.isCorrectAnswer));
    }

    let filteredTerms = newTerms;

    if (
      selectedGroups.length > 0 &&
      selectedGroups.some((group) => group.title !== "all")
    ) {
      const groupNameSet = new Set(selectedGroups.map((group) => group.title));
      filteredTerms = newTerms.filter((term) => groupNameSet.has(term.group));

      setNoTermsInSelectedGroups(filteredTerms.length === 0);
    }

    setFilteredTerms(filteredTerms);
    setLearningReviewLatestAnswerTypeToLocalStroage(currentSelectedAnswerType);
  }

  async function fetchLeanredTerms() {
    try {
      const result = await fetchLeanredTermsFromAPI(currentSelectedRange);
      const newTerms = result.learnedTerms;
      setOriginTerms(newTerms);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function updateIntervalUnitCount(count: number) {
    setLearningIntervalUnitCountToLocalStroage(count);
    updateIntervalUnitCountString();
  }

  function updateIntervalUnitCountString() {
    const countValue = getLearningIntervalUnitCountFromLocalStorage();
    const countDecimalString = toDecimalString(countValue);
    let string = "";
    if (Number(countValue) === NO_LIMIT) {
      string = t("LearningIntervalUnitCountAllType");
    } else {
      string = t("LearningIntervalUnitCountValueType", {
        value: countDecimalString,
      });
    }
    setIntervalUnitCountString(string);
  }

  function setAnswerType(answerType: LearningAnswerType, checked: boolean) {
    if (!checked) {
      // 선택 해제
      const filtered = currentSelectedAnswerType.filter(
        (t) => t !== answerType
      );
      setCurrentSelectedAnswerType(filtered);
    } else {
      // 새로 선택
      const current = [...currentSelectedAnswerType];
      current.push(answerType);
      setCurrentSelectedAnswerType(current);
    }

    // 선택 해제 후 아무것도 남지 않으면 다시 선택
    if (currentSelectedAnswerType.length === 0) {
      const current = [...currentSelectedAnswerType];
      current.push(answerType);
      setCurrentSelectedAnswerType(current);
    }
  }

  function handleOnClickReviewContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickLearningRangeSettingView(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenLearningRange(true);
  }

  function handleOnClickIntervalUnitCountButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenIntervalUnitCountPicker(true);
  }

  function handleOnClickGroupSettingView(e: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(e.currentTarget);
    setOpenGroupSelect(true);
  }

  function handleOnClickLearningTypeSettingView(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenLearningType(true);
  }

  function handleOnClickAnswerTypeSettingView(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenAnswerType(true);
  }

  if (!props.open) return null;

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <div
            className={styles.reviewContainer}
            onClick={handleOnClickReviewContainer}
          >
            <div className={styles.contentsContainer}>
              <ContainerNavigationView
                open={true}
                title={t("LearningReviewButton")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={() => {
                  props.onClose();
                }}
              />

              <div className={styles.reviewTermCountContainer}>
                <span className={styles.reviewTermCountText}>
                  {parts[0]}
                  <span className={styles.reviewTermCountHighlightText}>
                    {termCount}
                  </span>
                  {parts[1]}
                </span>
              </div>

              <div className={styles.settingContainer}>
                <ListSettingView
                  title={t("LearningReviewRange")}
                  value={getLearningRangeText(currentSelectedRange)}
                  valueStyle="stringValue"
                  showingDivider={true}
                  handleOnClick={handleOnClickLearningRangeSettingView}
                />
                <ListSettingView
                  title={t("LearningIntervalUnitCountSettingTitle")}
                  description={t("QuestionPerSectionCountSettingTitle")}
                  value={intervalUnitCountString}
                  valueStyle="stringValue"
                  handleOnClick={handleOnClickIntervalUnitCountButton}
                />
              </div>

              <div className={styles.settingContainer}>
                <ListSettingView
                  title={t("GroupSelectToLearn")}
                  leftIcon={noTermsInSelectedGroups ? AlertIcon : undefined}
                  leftIconColor={
                    noTermsInSelectedGroups
                      ? "var(--level-one-color)"
                      : undefined
                  }
                  value={selectedGroupsString}
                  valueStyle="stringValue"
                  handleOnClick={handleOnClickGroupSettingView}
                />
              </div>

              <div className={styles.settingContainer}>
                <ListSettingView
                  title={t("LearningReviewModeTitle")}
                  value={getLearningTypeText(currentSelectedLearningType)}
                  valueStyle="stringValue"
                  showingDivider={true}
                  handleOnClick={handleOnClickLearningTypeSettingView}
                />
                <ListSettingView
                  title={t("LearningReviewTargetTitle")}
                  value={getLearningAnswerTypeText(currentSelectedAnswerType)}
                  valueStyle="stringValue"
                  handleOnClick={handleOnClickAnswerTypeSettingView}
                />
              </div>

              <BigButton
                title={t("Start")}
                variant={filteredTerms.length === 0 ? "disable" : "normal"}
                handleClick={() => {
                  if (filteredTerms.length === 0) return;
                  const item = new LearningRetry(
                    filteredTerms,
                    currentSelectedLearningType
                  );
                  props.onClickStartButton(item);
                  props.onClose();
                }}
              />
            </div>
          </div>
        </div>

        <CountPicker
          open={openIntervalUnitCountPicker}
          defaultValue={getLearningIntervalUnitCountFromLocalStorage()}
          style={CountPickerStyle.learningIntervalUnitCount}
          onComplete={(count: number) => updateIntervalUnitCount(count)}
          onClose={() => {
            setOpenIntervalUnitCountPicker(false);
          }}
        />

        <LearningReviewGroupSelectScreen
          open={openGroupSelect}
          terms={originTerms}
          selectedAnswerTypes={currentSelectedAnswerType}
          onSelectAllGroup={() => {
            setSelectedGroups([]);
          }}
          onSelectGroups={(groups: Group[]) => {
            setSelectedGroups(groups);
          }}
          onClose={() => {
            setOpenGroupSelect(false);
          }}
        />

        <OnevocaCheckBoxContainer
          open={openAnswerType}
          anchorEl={anchorEl}
          needCheckOneOrMore={true}
          items={[
            {
              title: t("CorrectAnswer"),
              checked: currentSelectedAnswerType.includes(
                LearningAnswerType.correct
              ),
              onChangeValue(checked) {
                setAnswerType(LearningAnswerType.correct, checked);
              },
            },
            {
              title: t("IncorrectAnswer"),
              checked: currentSelectedAnswerType.includes(
                LearningAnswerType.incorrect
              ),
              onChangeValue(checked) {
                setAnswerType(LearningAnswerType.incorrect, checked);
              },
            },
          ]}
          onClose={() => {
            setOpenAnswerType(false);
          }}
        />

        <BasicMenu
          isOpen={openLearningRange}
          anchorEl={anchorEl}
          buttons={[
            {
              title: getLearningRangeText(LearningHistoryRange.day),
              handleOnClose: () => {
                setOpenLearningRange(false);
                setCurrentSelectedRange(LearningHistoryRange.day);
                setLearningReviewLatestRangeToLocalStroage(
                  LearningHistoryRange.day
                );
              },
            },
            {
              title: getLearningRangeText(LearningHistoryRange.yesterday),
              handleOnClose: () => {
                setOpenLearningRange(false);
                setCurrentSelectedRange(LearningHistoryRange.yesterday);
                setLearningReviewLatestRangeToLocalStroage(
                  LearningHistoryRange.yesterday
                );
              },
            },
            {
              title: getLearningRangeText(LearningHistoryRange.three_days),
              handleOnClose: () => {
                setOpenLearningRange(false);
                setCurrentSelectedRange(LearningHistoryRange.three_days);
                setLearningReviewLatestRangeToLocalStroage(
                  LearningHistoryRange.three_days
                );
              },
            },
            {
              title: getLearningRangeText(LearningHistoryRange.week),
              handleOnClose: () => {
                setOpenLearningRange(false);
                setCurrentSelectedRange(LearningHistoryRange.week);
                setLearningReviewLatestRangeToLocalStroage(
                  LearningHistoryRange.week
                );
              },
            },
            {
              title: getLearningRangeText(LearningHistoryRange.month),
              handleOnClose: () => {
                setOpenLearningRange(false);
                setCurrentSelectedRange(LearningHistoryRange.month);
                setLearningReviewLatestRangeToLocalStroage(
                  LearningHistoryRange.month
                );
              },
            },
            {
              title: getLearningRangeText(LearningHistoryRange.all),
              handleOnClose: () => {
                setOpenLearningRange(false);
                setCurrentSelectedRange(LearningHistoryRange.all);
                setLearningReviewLatestRangeToLocalStroage(
                  LearningHistoryRange.all
                );
              },
            },
          ]}
          handleOnClose={() => {
            setOpenLearningRange(false);
          }}
        />

        <BasicMenu
          isOpen={openLearningType}
          anchorEl={anchorEl}
          buttons={[
            {
              title: t("Flashcard"),
              handleOnClose: () => {
                setOpenLearningType(false);
                setCurrentSelectedLearningType(LearningType.flashcards);
                setLearningReviewLatestType(LearningType.flashcards);
              },
            },
            {
              title: t("MultipleChoiceTitle"),
              handleOnClose: () => {
                setOpenLearningType(false);
                setCurrentSelectedLearningType(LearningType.multiplechoice);
                setLearningReviewLatestType(LearningType.multiplechoice);
              },
            },
            {
              title: t("SpellingQuiz"),
              handleOnClose: () => {
                setOpenLearningType(false);
                setCurrentSelectedLearningType(LearningType.spellingQuiz);
                setLearningReviewLatestType(LearningType.spellingQuiz);
              },
            },
          ]}
          handleOnClose={() => {
            setOpenLearningType(false);
          }}
        />
      </div>
    </>
  );
}
